import { CustomerLiteModel, useGetCustomers } from 'api-hooks/customer';
import { CircleSVG } from 'common/assets';
import { NavigationRoutes } from 'common/constants';
import { UserStatusEnum } from 'common/constants/enum';
import { themedStyled } from 'common/styles/styletron.theme';
import { string2money, transformLabel } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import Text from 'components/elements/text';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import { Container, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import CustomerTable from './components/table';

export default function CustomerList() {
  const { t } = useTranslation();
  const { queryPage } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);

  const _columns = React.useMemo<IColumn<CustomerLiteModel>[]>(
    () => [
      {
        header: t('common:name'),
        accessorKey: 'name',
        minSize: 250,
        sortName: 'name',
        cell: ({ row }) => {
          const { original } = row;
          return (
            <TitleContainer>
              <Text variant="LabelSmall">
                {transformLabel([
                  original.title && t(`enum:${original.title}`),
                  original.name,
                ])}
              </Text>
              {original.status === UserStatusEnum.UnderSurveillance && (
                <CircleSVG />
              )}
            </TitleContainer>
          );
        },
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 180,
      },
      {
        header: t('common:alias'),
        accessorKey: 'alias',
        minSize: 128,
        sortName: 'alias',
      },
      {
        header: t('common:balance'),
        accessorKey: 'balance',
        sortName: 'balance',
        minSize: 150,
        cell: ({ getValue }) => <>{string2money(getValue() as number)}</>,
      },
      {
        header: t('common:address'),
        accessorKey: 'address',
        minSize: 180,
      },
      {
        header: t('common:email'),
        accessorKey: 'email',
        minSize: 180,
      },
      {
        header: t('cruise:tel_phone'),
        accessorKey: 'phone',
        minSize: 180,
      },
      {
        header: t('cruise:cellphone_number'),
        accessorKey: 'phone_2',
        minSize: 180,
      },
      {
        header: t('common:billing_address'),
        accessorKey: 'billings',
        minSize: 250,
        cell: ({ row }) => {
          const { original } = row;
          return (
            <>{`${(original.billings || [])
              .map((billing) => billing.address)
              .join(',')}`}</>
          );
        },
      },
      {
        header: t('common:created_by'),
        accessorKey: 'createdBy.name',
        minSize: 150,
        sortName: 'created_by',
        cell: ({ getValue }) => <>{getValue() as string}</>,
      },
    ],
    [t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetCustomers,
    {
      params: {
        page,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('customers');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:customer')}
          showAdd={permission.create}
          onRefresh={refetch}
          linkAdd={NavigationRoutes.CustomersCreate}
        />
        <TopContainer>
          <QueryFilter filters={filters} isUseResponseOptions />
        </TopContainer>
        <CustomerTable
          customers={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}

const TitleContainer = themedStyled('div', {
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: '8px',
});
